import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Download, FileText, FileDown, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: "chapters", label: "Story Chapters", icon: "scroll" },
  { key: "characters", label: "Characters", icon: "users" },
  { key: "creatures", label: "Bestiary", icon: "skull" },
  { key: "lore", label: "Lore", icon: "book" },
  { key: "deities", label: "Deities", icon: "star" },
  { key: "locations", label: "Locations & Buildings", icon: "castle" },
  { key: "sessions", label: "Session Logs", icon: "clock" },
  { key: "notes", label: "Notes (Private + Party)", icon: "notepad" },
];

const ExportCampaign = ({ campaignId, campaignName }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(SECTIONS.map(s => s.key));
  const [format, setFormat] = useState("pdf");
  const [exporting, setExporting] = useState(false);

  const toggleSection = (key) => {
    setSelected(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleAll = () => {
    if (selected.length === SECTIONS.length) {
      setSelected([]);
    } else {
      setSelected(SECTIONS.map(s => s.key));
    }
  };

  const handleExport = async () => {
    if (selected.length === 0) {
      toast.error("Select at least one section to export");
      return;
    }
    setExporting(true);
    try {
      // Step 1: Fetch the file as a blob directly
      const res = await axios.post(
        `${API}/campaigns/${campaignId}/export`,
        { sections: selected, format },
        { withCredentials: true }
      );
      const { download_token } = res.data;
      const ext = format === "docx" ? "docx" : "pdf";
      const filename = `${campaignName.replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_")}_Export.${ext}`;

      // Step 2: Fetch the actual file as a blob
      const fileRes = await axios.get(
        `${API}/campaigns/download/${download_token}`,
        { responseType: "blob" }
      );

      // Step 3: Create blob URL and trigger download
      const blob = new Blob([fileRes.data]);
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Clean up after a delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 5000);

      toast.success(`${filename} saved to your Downloads folder`);
      setOpen(false);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail, {
          action: { label: "Upgrade", onClick: () => window.location.href = "/subscription" }
        });
      } else {
        toast.error("Export failed. Please try again.");
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" data-testid="export-campaign-button">
          <Download className="w-4 h-4 mr-1" /> Export
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Export Campaign</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-4">
          {/* Format */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Format</p>
            <div className="flex gap-2">
              <button
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm border transition-colors ${
                  format === "pdf"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-muted-foreground"
                }`}
                onClick={() => setFormat("pdf")}
                data-testid="format-pdf"
              >
                <FileText className="w-5 h-5" />
                <span className="text-sm font-medium">PDF</span>
              </button>
              <button
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm border transition-colors ${
                  format === "docx"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-muted-foreground"
                }`}
                onClick={() => setFormat("docx")}
                data-testid="format-docx"
              >
                <FileDown className="w-5 h-5" />
                <span className="text-sm font-medium">Word</span>
              </button>
            </div>
          </div>

          {/* Sections */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Include Sections</p>
              <button
                className="text-xs text-primary hover:underline"
                onClick={toggleAll}
                data-testid="toggle-all-sections"
              >
                {selected.length === SECTIONS.length ? "Deselect All" : "Select All"}
              </button>
            </div>
            <div className="space-y-1">
              {SECTIONS.map(section => (
                <label
                  key={section.key}
                  className={`flex items-center gap-3 px-3 py-2 rounded-sm cursor-pointer transition-colors ${
                    selected.includes(section.key) ? "bg-primary/5" : "hover:bg-muted/20"
                  }`}
                  data-testid={`section-toggle-${section.key}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(section.key)}
                    onChange={() => toggleSection(section.key)}
                    className="accent-primary w-4 h-4"
                  />
                  <span className={`text-sm ${selected.includes(section.key) ? "text-foreground" : "text-muted-foreground"}`}>
                    {section.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Export button */}
          <Button
            onClick={handleExport}
            disabled={exporting || selected.length === 0}
            className="w-full bg-primary text-primary-foreground rounded-sm"
            data-testid="confirm-export-button"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export {format.toUpperCase()} ({selected.length} sections)
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportCampaign;
